import { EventType, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LocalStorageItems } from 'shared/constants';
import { useAppDispatch } from 'shared/hooks';

import { AUTH_SCOPES } from '../authConfig';
import { fetchAvailableTenantsThunk, fetchUserThunk, initialState, setAccessToken, setCurrentCandidateEmail } from '../store/authSlice';
import { parseJwtToken, TokenInfo } from '../utils';

export default function useLoadInitialItemsOnLogin(
    instance: IPublicClientApplication,
    inProgress: InteractionStatus
) {
    const dispatch = useAppDispatch();
    const [ initialized, setInitialized ] = useState(false);
    const isCheckingRef = useRef(false);

    const setTenant = useCallback((parsedToken: TokenInfo) => {
        if (parsedToken.globusTenant && !localStorage.getItem(LocalStorageItems.TENANT_ID)) {
            localStorage.setItem(LocalStorageItems.TENANT_ID, parsedToken.globusTenant);
        }
    }, []);

    const setEmail = useCallback((parsedToken: TokenInfo) => {
        if (parsedToken.email) {
            dispatch(setCurrentCandidateEmail(parsedToken.email));
        }
    }, [ dispatch ]);

    const setAuthConfig = useCallback((accessToken: string) => {
        const newConfig = {
            token: accessToken,
            fullAccess: true,
            oldAvailabilityToken: initialState.accessToken.oldAvailabilityToken
        };

        dispatch(setAccessToken(newConfig));
    }, [ dispatch ]);

    const processToken = useCallback((accessToken: string): boolean => {
        const parsedToken = parseJwtToken(accessToken);

        if (!parsedToken) {
            return false;
        }

        setTenant(parsedToken);
        setEmail(parsedToken);
        setAuthConfig(accessToken);

        dispatch(fetchUserThunk());
        dispatch(fetchAvailableTenantsThunk());

        setInitialized(true);

        return true;
    }, [ dispatch, setTenant, setEmail, setAuthConfig ]);

    // Check for existing accounts when inProgress becomes 'none'
    // This handles the race condition where ACQUIRE_TOKEN_SUCCESS fires before listener is registered
    useEffect(() => {
        if (initialized || inProgress !== 'none' || isCheckingRef.current) {
            return;
        }

        const checkExistingAuth = async () => {
            const accounts = instance.getAllAccounts();

            if (accounts.length === 0) {
                return;
            }

            isCheckingRef.current = true;

            try {
                const result = await instance.acquireTokenSilent({
                    scopes: AUTH_SCOPES,
                    account: accounts[0]
                });

                processToken(result.accessToken);
            } catch {
                // Silent acquisition failed - will rely on event or need interactive login
            } finally {
                isCheckingRef.current = false;
            }
        };

        checkExistingAuth();
    }, [ instance, initialized, inProgress, processToken ]);

    // Event listener for token acquisitions (including future renewals)
    useEffect(() => {
        if (initialized) {
            return;
        }

        const callbackId = instance.addEventCallback((event) => {
            if (event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS || !event.payload || !('accessToken' in event.payload)) {
                return;
            }

            processToken(event.payload.accessToken);
        }, [ EventType.ACQUIRE_TOKEN_SUCCESS ]);

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [ instance, initialized, processToken ]);
}
